import { ScreenSaverMediaType } from '@/enums/home';
import { useAccessibilityTTS, useDialogCheck, useOrientation } from '@/hooks';
import { useConfigStore, useHomeStore } from '@/stores';
import type { CacheScreenSaverInfo } from '@/types';
import useEmblaCarousel from 'embla-carousel-react';
import clsx from 'clsx';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { PATHS } from '@/router/paths';
import { TrackingService } from '@/services/analytics/TrackingService';
import { useTranslation } from 'react-i18next';

type Props = {
    screenSaver: CacheScreenSaverInfo[];
    excludeFromFocusOrder?: boolean;
};

const SCREEN_SAVER_TTS_INTERVAL_MS = 2 * 60 * 1000;

// Mốc an toàn phòng trường hợp sự kiện `settle` của carousel không bắn ra
const SLIDE_SETTLE_FALLBACK_MS = 1000;

export const HomePageSwiper = ({ screenSaver, excludeFromFocusOrder = false }: Props) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const { speakAnnouncement, stopSpeaking } = useAccessibilityTTS();

    const { isHorizontal } = useOrientation();
    const isZoomIn = useConfigStore((state) => state.config.ui.zoomIn);
    const isZoomClosed = useConfigStore((state) => state.config.ui.zoomClosed);
    const screenMode = useConfigStore((s) => s.config?.ui?.screenMode);
    const isScreenSaverFullscreen = useConfigStore((s) => s.config?.ui?.isScreenSaverFullscreen);
    const isDialogOpen = useDialogCheck();
    const isHomeMenuVisible = useHomeStore((state) => state.isHomeMenuVisible);
    const setHomeMenuVisible = useHomeStore((state) => state.setHomeMenuVisible);

    const ttsIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const hasUserInteractedRef = useRef(false);
    const isHomePage = location.pathname === PATHS.HOME.INDEX;

    const isZoomed = useMemo(() => {
        return isZoomIn || (isZoomClosed === false && isZoomIn === false);
    }, [isZoomIn, isZoomClosed]);

    const [viewPortRef, sliderApi] = useEmblaCarousel({
        containScroll: 'trimSnaps',
        dragFree: false,
        loop: true,
        watchDrag: !isZoomed, // Disable drag when zoomed in
    });

    const [currentSlide, setCurrentSlide] = useState(0);
    const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const [mediaErrors, setMediaErrors] = useState<Set<string>>(new Set());

    const [isSliding, setIsSliding] = useState(false);
    const isSlidingRef = useRef(false);
    const slideSettleFallbackRef = useRef<NodeJS.Timeout | null>(null);

    // Chỉ quan tâm trạng thái sliding khi slide hiện tại là video, tránh ảnh hưởng timer của slide ảnh
    const isSlidingIntoVideo =
        isSliding && screenSaver?.[currentSlide]?.originalData?.mediaType === ScreenSaverMediaType.VIDEO;

    const handleMediaError = useCallback((itemId: string) => {
        setMediaErrors((prev) => new Set(prev).add(itemId));
        console.warn(`Media error for ${itemId}, falling back to original URL`);
    }, []);

    const getMediaUrl = useCallback(
        (item: CacheScreenSaverInfo) => {
            if (mediaErrors.has(item.id) || !item.localPath) {
                return item.originalData.fileUrl;
            }
            return `/screen-savers/${item.localPath}`;
        },
        [mediaErrors]
    );

    const goToNextSlide = useCallback(() => {
        if (isZoomed) {
            return;
        }

        if (!Array.isArray(screenSaver) || !screenSaver?.length) {
            return;
        }

        // Nếu chỉ có 1 slide, restart video nếu là video
        if (screenSaver.length === 1) {
            const currentVideoRefs = videoRefs.current;
            const video = currentVideoRefs[0];
            if (video) {
                video.currentTime = 0;
                const playPromise = video.play();
                if (playPromise !== undefined) {
                    playPromise.catch(() => {});
                }
            }
            return;
        }

        // Chuyển sang slide tiếp theo nếu có nhiều hơn 1 slide
        if (sliderApi && screenSaver.length > 1) {
            const nextIndex = (currentSlide + 1) % screenSaver.length;
            sliderApi.scrollTo(nextIndex);
            setCurrentSlide(nextIndex);
        }
    }, [sliderApi, currentSlide, screenSaver, isZoomed]);

    const handleSlideChange = useCallback(() => {
        if (sliderApi) {
            const selectedIndex = sliderApi.selectedScrollSnap();
            setCurrentSlide(selectedIndex);
        }
    }, [sliderApi]);

    useEffect(() => {
        if (sliderApi) {
            sliderApi.on('select', handleSlideChange);
            return () => {
                sliderApi.off('select', handleSlideChange);
            };
        }
    }, [sliderApi, isZoomed, handleSlideChange]);

    // Theo dõi khoảng thời gian carousel đang trượt để giữ khung hình video ổn định khi chuyển slide
    useEffect(() => {
        if (!sliderApi) return;

        const clearFallback = () => {
            if (slideSettleFallbackRef.current) {
                clearTimeout(slideSettleFallbackRef.current);
                slideSettleFallbackRef.current = null;
            }
        };

        const handleSlideSettle = () => {
            clearFallback();
            isSlidingRef.current = false;
            setIsSliding(false);

            // Sliding xong mới trả các video đã rời màn hình về khung hình đầu
            const settledIndex = sliderApi.selectedScrollSnap();
            videoRefs.current.forEach((video, index) => {
                if (!video || index === settledIndex) return;
                video.pause();
                if (video.currentTime !== 0) {
                    video.currentTime = 0;
                }
            });
        };

        const handleSlideStart = () => {
            clearFallback();
            isSlidingRef.current = true;
            setIsSliding(true);
            slideSettleFallbackRef.current = setTimeout(handleSlideSettle, SLIDE_SETTLE_FALLBACK_MS);
        };

        sliderApi.on('select', handleSlideStart);
        sliderApi.on('settle', handleSlideSettle);

        return () => {
            sliderApi.off('select', handleSlideStart);
            sliderApi.off('settle', handleSlideSettle);
            clearFallback();
            isSlidingRef.current = false;
        };
    }, [sliderApi]);

    useEffect(() => {
        setMediaErrors(new Set());
    }, [screenSaver]);

    useEffect(() => {
        if (!Array.isArray(screenSaver) || !screenSaver.length || isZoomed) {
            return;
        }

        const currentItem = screenSaver[currentSlide];

        if (!currentItem) return;
        const currentVideoRefs = videoRefs.current;

        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }

        if (currentItem?.originalData?.mediaType === ScreenSaverMediaType.VIDEO) {
            const video = currentVideoRefs[currentSlide];
            if (video) {
                // Đảm bảo video đã load trước khi play
                const handleVideoCanPlay = () => {
                    video.currentTime = 0;
                    const playPromise = video.play();
                    if (playPromise !== undefined) {
                        playPromise.catch(() => {});
                    }
                };

                const handleVideoEnd = () => {
                    goToNextSlide();
                };

                if (isSlidingIntoVideo) {
                    // Đang trượt vào: giữ khung hình đầu, sliding xong mới phát
                    video.pause();
                    if (video.currentTime !== 0) {
                        video.currentTime = 0;
                    }
                } else if (video.readyState >= 2) {
                    // Nếu video đã sẵn sàng, play ngay
                    handleVideoCanPlay();
                } else {
                    video.addEventListener('canplay', handleVideoCanPlay, { once: true });
                }

                video.addEventListener('ended', handleVideoEnd);

                return () => {
                    video.removeEventListener('canplay', handleVideoCanPlay);
                    video.removeEventListener('ended', handleVideoEnd);
                    video.pause();
                    // Đang trượt ra: giữ khung hình cuối, reset sẽ chạy sau khi sliding kết thúc
                    if (!isSlidingRef.current) {
                        video.currentTime = 0;
                    }
                };
            }
        } else if (currentItem?.originalData?.mediaType === ScreenSaverMediaType.IMAGE) {
            // Nếu có playtimeSec thì dùng, nếu không thì dùng giá trị mặc định 5 giây
            const playtime = currentItem?.originalData?.playtimeSec || 5;
            timeoutRef.current = setTimeout(() => {
                goToNextSlide();
            }, playtime * 1000);
        }

        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
        };
    }, [currentSlide, goToNextSlide, screenSaver, isZoomed, isSlidingIntoVideo]);

    // Cleanup on unmount
    useEffect(() => {
        const currentVideoRefs = videoRefs.current;

        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
            // Pause all videos
            currentVideoRefs.forEach((video) => {
                if (video) {
                    video.pause();
                    video.currentTime = 0;
                }
            });
        };
    }, []);

    const speakScreenSaverDetail = useCallback(() => {
        if (!isHomePage || isDialogOpen || !isScreenSaverFullscreen || isHomeMenuVisible) return;
        speakAnnouncement(t('home.detail.tts_desc'));
    }, [isHomePage, isDialogOpen, isHomeMenuVisible, isScreenSaverFullscreen, speakAnnouncement, t]);

    useEffect(() => {
        if (!isHomePage || isHomeMenuVisible || !isScreenSaverFullscreen) {
            stopSpeaking();
            if (ttsIntervalRef.current) {
                clearInterval(ttsIntervalRef.current);
                ttsIntervalRef.current = null;
            }
            return;
        }

        const startTimer = window.setTimeout(() => {
            if (location.pathname !== PATHS.HOME.INDEX) return;
            speakScreenSaverDetail();
            ttsIntervalRef.current = setInterval(() => {
                speakScreenSaverDetail();
            }, SCREEN_SAVER_TTS_INTERVAL_MS);
        }, 500);

        return () => {
            window.clearTimeout(startTimer);
            if (ttsIntervalRef.current) {
                clearInterval(ttsIntervalRef.current);
                ttsIntervalRef.current = null;
            }
            stopSpeaking();
        };
    }, [isHomePage, isHomeMenuVisible, isScreenSaverFullscreen, speakScreenSaverDetail, stopSpeaking, location.pathname]);

    useEffect(() => {
        const handleUserInteraction = () => {
            stopSpeaking();
        };

        window.addEventListener('pointerdown', handleUserInteraction, true);
        window.addEventListener('touchstart', handleUserInteraction, true);
        window.addEventListener('keydown', handleUserInteraction, true);

        return () => {
            window.removeEventListener('pointerdown', handleUserInteraction, true);
            window.removeEventListener('touchstart', handleUserInteraction, true);
            window.removeEventListener('keydown', handleUserInteraction, true);
        };
    }, [stopSpeaking]);

    const mediaHeightClass = useMemo(() => {
        if (isScreenSaverFullscreen) return 'h-screen';

        if (isHorizontal || (isHorizontal && screenMode === 'split')) return 'h-[calc(879*var(--vh-unit))]';

        if (!isHorizontal && screenMode === 'split') return 'h-[calc(1756*var(--vh-unit))]';
        return 'h-[calc(1700*var(--vh-unit))]';
    }, [isHorizontal, screenMode, isScreenSaverFullscreen]);

    const onClickScreenSaver = () => {
        if (!isScreenSaverFullscreen) return;

        hasUserInteractedRef.current = true;
        stopSpeaking();

        if (!isHomeMenuVisible) {
            setHomeMenuVisible(true);
            return;
        }
        TrackingService.trackMenuByRoute(PATHS.FLOOR_GUIDE.INDEX);
        navigate(PATHS.FLOOR_GUIDE.INDEX);
        setHomeMenuVisible(false);
    };

    return (
        <div className="h-full w-full overflow-hidden" ref={viewPortRef}>
            <div className="flex h-full w-full" onClick={onClickScreenSaver}>
                {Array.isArray(screenSaver) && screenSaver.length > 0 ? (
                    screenSaver.map((item, index) => (
                        <div key={item.id} className="relative h-full w-full flex-shrink-0">
                            {item?.originalData?.mediaType === ScreenSaverMediaType.IMAGE && (
                                <img
                                    className={clsx('w-full object-cover', mediaHeightClass)}
                                    src={getMediaUrl(item)}
                                    alt={item.id}
                                    tabIndex={excludeFromFocusOrder ? -1 : undefined}
                                    aria-hidden={excludeFromFocusOrder ? true : undefined}
                                    onError={() => handleMediaError(item.id)}
                                />
                            )}
                            {item?.originalData?.mediaType === ScreenSaverMediaType.VIDEO && (
                                <video
                                    ref={(el) => {
                                        videoRefs.current[index] = el;
                                    }}
                                    className={clsx('w-full object-cover', mediaHeightClass)}
                                    src={getMediaUrl(item)}
                                    autoPlay={false}
                                    preload="auto"
                                    muted
                                    playsInline
                                    tabIndex={excludeFromFocusOrder ? -1 : undefined}
                                    aria-hidden={excludeFromFocusOrder ? true : undefined}
                                    onError={() => handleMediaError(item.id)}
                                />
                            )}
                        </div>
                    ))
                ) : (
                    <div className="relative h-full w-full flex-shrink-0" />
                )}
            </div>
        </div>
    );
};
